import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useInvestorProcessing } from "@/hooks/investor";
import { useEmailManagement } from "@/hooks/useEmailManagement";
import { ZoneDistributionChart } from "@/components/dashboard/ZoneDistributionChart";
import { SpeedometerChart } from "@/components/dashboard/SpeedometerChart";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Database, Mail, FileUp, Loader2 } from "lucide-react";

const AnalyticsHub = () => {
  const { investors, contacts, loadSavedData, isInvestorProcessed, isLoadingData } = useInvestorProcessing();
  const { emailStats, loadEmailData } = useEmailManagement();
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    console.log('AnalyticsHub: Starting data load');
    const loadData = async () => {
      try {
        await Promise.all([
          loadSavedData(),
          loadEmailData()
        ]);
        console.log('AnalyticsHub: Data load completed');
      } catch (error) {
        console.error('AnalyticsHub: Error loading data:', error);
      } finally {
        setIsInitialLoad(false);
      }
    };
    
    loadData();
  }, []);

  // Calculate zone distributions based on relevance scores
  const calculateZoneDistribution = () => {
    if (!investors.length) return { green: 0, yellow: 0, orange: 0, red: 0 };
    
    let green = 0, yellow = 0, orange = 0, red = 0;
    let processedCount = 0;
    
    investors.forEach(investor => {
      if (isInvestorProcessed(investor)) {
        processedCount++;
        const score = investor.relevanceScore || 0;
        
        if (score >= 7.5) green++;
        else if (score >= 5) yellow++;
        else if (score >= 2.5) orange++;
        else red++;
      }
    });
    
    const total = processedCount || 1;
    return {
      green: Math.round((green / total) * 100),
      yellow: Math.round((yellow / total) * 100),
      orange: Math.round((orange / total) * 100),
      red: Math.round((red / total) * 100),
      processedCount
    };
  };

  const zoneDistribution = calculateZoneDistribution();

  // Show loading state only for initial data load
  if (isInitialLoad && (isLoadingData || investors.length === 0)) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto" />
          <p className="text-sm text-gray-600">Loading analytics data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Analytics Hub</h1>
          <p className="text-muted-foreground">
            Monitor your investor outreach performance and data processing progress.
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button asChild variant="outline">
            <Link to="/data">
              <Database className="h-4 w-4 mr-2" />
              Manage Data
            </Link>
          </Button>
          <Button asChild>
            <Link to="/email">
              <Mail className="h-4 w-4 mr-2" />
              Create Emails
            </Link>
          </Button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">VCs Listed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{investors.length}</div>
            {investors.length === 0 && (
              <Button asChild variant="link" className="p-0 h-auto text-xs mt-1">
                <Link to="/data">
                  <FileUp className="h-3 w-3 mr-1" />
                  Upload data
                </Link>
              </Button>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">VCs Processed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{zoneDistribution.processedCount}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {Math.round((zoneDistribution.processedCount / (investors.length || 1)) * 100)}% of total
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Emails Sent</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{emailStats.totalSent}</div>
            {emailStats.totalSent === 0 && investors.length > 0 && (
              <Button asChild variant="link" className="p-0 h-auto text-xs mt-1">
                <Link to="/email">
                  <Mail className="h-3 w-3 mr-1" />
                  Start outreach
                </Link>
              </Button>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Responses Received</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{emailStats.totalResponses}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {emailStats.responseRate.toFixed(1)}% response rate
            </p>
          </CardContent>
        </Card>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="col-span-1">
          <CardHeader>
            <CardTitle>VC Relevance Distribution</CardTitle>
            <CardDescription>
              {zoneDistribution.processedCount > 0 
                ? "Percentage of processed VCs by relevance zone" 
                : "No VCs have been processed yet"}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-2">
            <ZoneDistributionChart distribution={zoneDistribution} />
            {zoneDistribution.processedCount === 0 && (
              <div className="text-center mt-4">
                <p className="text-muted-foreground mb-2">
                  Process VCs to see relevance data
                </p>
                <Button asChild variant="outline" size="sm">
                  <Link to="/data?tab=investors">
                    Process Investors
                  </Link>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
        
        <Card className="col-span-1">
          <CardHeader>
            <CardTitle>Response Rate</CardTitle>
            <CardDescription>Email response conversion rate</CardDescription>
          </CardHeader>
          <CardContent className="pt-2 flex justify-center">
            <SpeedometerChart percentage={emailStats.responseRate} />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>
            Common workflows to get you started
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button asChild variant="outline" className="h-auto p-4 flex-col">
              <Link to="/data?tab=upload">
                <FileUp className="h-6 w-6 mb-2" />
                <span className="font-medium">Upload New Data</span>
                <span className="text-xs text-muted-foreground mt-1">
                  Import investor lists
                </span>
              </Link>
            </Button>
            
            <Button asChild variant="outline" className="h-auto p-4 flex-col">
              <Link to="/data?tab=investors">
                <Database className="h-6 w-6 mb-2" />
                <span className="font-medium">Process Investors</span>
                <span className="text-xs text-muted-foreground mt-1">
                  Enrich and analyze
                </span>
              </Link>
            </Button>
            
            <Button asChild variant="outline" className="h-auto p-4 flex-col">
              <Link to="/email">
                <Mail className="h-6 w-6 mb-2" />
                <span className="font-medium">Create Campaign</span>
                <span className="text-xs text-muted-foreground mt-1">
                  Generate personalized emails
                </span>
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsHub;
